import { useState, useCallback, useEffect } from 'react';
import type { ProductDetail, ProductDetailVariant, ComboVariantOption, ProcessedComboProduct, WidgetAuth } from '../types/api';
import { apiClient } from '../services/api';

interface UseComboProductsReturn {
  comboProducts: ProcessedComboProduct[];  // Loaded and processed combo products
  isLoading: boolean;                      // true while fetching child products
  error: string | null;                    // Error message if any child product fails to load
  selectedComboVariants: Record<number, ProductDetailVariant>; // Map of combo_product_id -> selected variant
  updateComboVariant: (comboProductId: number, optionName: string, optionValue: string | number) => void;
  hasComboProducts: boolean;               // true if parent product has non-empty combo_products.ids
  comboDiscount: string;                   // Parent's combo_product_discount value
  reset: () => void;                       // Reset all combo state (called when parent product changes)
}

// ─── Pure Processing Functions ─────────────────────────────────────────────

/**
 * Filter out "Default Title" single-variant products and sort by price ascending.
 * Mirrors Angular widget `processComboProduct` variant filtering logic.
 */
function filterAndSortVariants(variants: ProductDetailVariant[]): ProductDetailVariant[] {
  const filtered = variants.filter(
    (v) => !(variants.length === 1 && v.name?.toLowerCase() === 'default title')
  );
  return [...filtered].sort((a, b) => {
    const priceA = parseFloat(String(a.price).replace(/[^0-9.]/g, '')) || 0;
    const priceB = parseFloat(String(b.price).replace(/[^0-9.]/g, '')) || 0;
    return priceA - priceB;
  });
}

/**
 * Process a single combo variant option from a child product's default variant.
 *
 * Rules (from Angular widget processComboProductOption):
 * 1. If option value matches any value in parent variant's `options` array
 *    (the variant dimension options like Size, Color — NOT product_options add-on options),
 *    mark as hidden with combo_product_default_value set to option value.
 * 2. If option value matches parent or child product name (case-insensitive),
 *    mark as hidden with combo_product_default_value set to option value.
 * 3. Otherwise: mark as visible with label from combo_configuration[childProduct.product_code].
 *
 * AC-08: Auto-hide matches against selectedVariant.options[] (dimension options), NOT product_options.
 * AC-09: Auto-hide matches against parent and child product name (case-insensitive includes).
 */
export function processComboProductOption(
  option: { name: string; value: string | number; available?: { name: string; value: string | number; disabled: boolean }[] },
  childProduct: ProductDetail,
  parentVariant: ProductDetailVariant,
  parentProduct: ProductDetail,
  comboDiscount: string
): ComboVariantOption {
  const optionValueStr = String(option.value).toLowerCase();

  // Check if option value matches any parent variant dimension option value
  // parentVariant.options is the array of {name, value} variant dimension pairs (Size, Color, etc.)
  const matchesParentVariantOption = parentVariant.options.some(
    (pOpt) => String(pOpt.value).toLowerCase() === optionValueStr
  );

  if (matchesParentVariantOption) {
    return {
      name: option.name,
      value: option.value,
      available: option.available,
      combo_product_id: childProduct.id,
      combo_product_hidden: true,
      combo_product_default_value: option.value,
      combo_product_discount: comboDiscount,
      combo_product_label: null,
    };
  }

  // Check if option value matches parent product name (case-insensitive includes)
  const parentName = (parentProduct.name || parentProduct.title || '').toLowerCase();
  const childName = (childProduct.name || childProduct.title || '').toLowerCase();

  if (
    (parentName && optionValueStr.includes(parentName)) ||
    (parentName && parentName.includes(optionValueStr)) ||
    (childName && optionValueStr.includes(childName)) ||
    (childName && childName.includes(optionValueStr))
  ) {
    return {
      name: option.name,
      value: option.value,
      available: option.available,
      combo_product_id: childProduct.id,
      combo_product_hidden: true,
      combo_product_default_value: option.value,
      combo_product_discount: comboDiscount,
      combo_product_label: null,
    };
  }

  // Visible option: get label from combo_configuration
  // combo_configuration keys are product_code strings; also try string(product.id) for robustness (Assumption A-3)
  const comboConfig = parentProduct.combo_products?.combo_configuration ?? {};
  const label =
    comboConfig[childProduct.product_code ?? ''] ||
    comboConfig[String(childProduct.id)] ||
    null;

  return {
    name: option.name,
    value: option.value,
    available: option.available,
    combo_product_id: childProduct.id,
    combo_product_hidden: false,
    combo_product_default_value: null,
    combo_product_discount: comboDiscount,
    combo_product_label: label,
  };
}

/**
 * Build the available option values for each option dimension across all variants.
 * Creates the `available` array for a given option name from filtered variants.
 */
function buildAvailableForOption(
  optionName: string,
  variants: ProductDetailVariant[]
): { name: string; value: string | number; disabled: boolean }[] {
  const seenValues = new Set<string>();
  const result: { name: string; value: string | number; disabled: boolean }[] = [];

  for (const variant of variants) {
    for (const opt of variant.options) {
      if (opt.name === optionName) {
        const key = String(opt.value);
        if (!seenValues.has(key)) {
          seenValues.add(key);
          result.push({
            name: opt.name,
            value: opt.value,
            disabled: variant.inventory === 0 && !variant.add_to_cart_when_out_of_stock,
          });
        }
      }
    }
  }

  return result;
}

/**
 * Process a child product into a ProcessedComboProduct.
 * Filters variants, selects default, processes combo options for each option dimension.
 */
export function processComboProduct(
  childProduct: ProductDetail,
  parentVariant: ProductDetailVariant,
  parentProduct: ProductDetail,
  comboDiscount: string
): ProcessedComboProduct {
  const rawVariants = childProduct.product_variants ?? [];
  const filteredVariants = filterAndSortVariants(rawVariants);

  // Default variant: first available (has inventory or allows OOS)
  const defaultVariant =
    filteredVariants.find((v) => v.inventory > 0 || v.add_to_cart_when_out_of_stock) ||
    filteredVariants[0];

  const comboVariantOptions: ComboVariantOption[] = [];

  if (defaultVariant) {
    for (const opt of defaultVariant.options) {
      // Build the available values for this option dimension from all filtered variants
      const available = buildAvailableForOption(opt.name, filteredVariants);

      const processed = processComboProductOption(
        { name: opt.name, value: opt.value, available },
        childProduct,
        parentVariant,
        parentProduct,
        comboDiscount
      );
      comboVariantOptions.push(processed);
    }
  }

  return {
    product: childProduct,
    comboVariantOptions,
    variants: filteredVariants,
  };
}

/**
 * Find the variant of a combo product that matches given option selections.
 * Used to update selectedComboVariants when user changes a dropdown.
 */
function findVariantForComboProduct(
  comboProduct: ProcessedComboProduct,
  optionSelections: Record<string, string | number>
): ProductDetailVariant | undefined {
  return comboProduct.variants.find((variant) =>
    variant.options.every((opt) => {
      // If option is hidden, use the default value; otherwise use user selection
      const comboOpt = comboProduct.comboVariantOptions.find((co) => co.name === opt.name);
      const expectedValue = comboOpt?.combo_product_hidden
        ? comboOpt.combo_product_default_value
        : optionSelections[opt.name];
      return expectedValue !== undefined && String(expectedValue) === String(opt.value);
    })
  );
}

/**
 * Build default option selections for a processed combo product.
 * Hidden options use their default_value; visible options use the first available value.
 */
function buildDefaultSelections(comboProduct: ProcessedComboProduct): Record<string, string | number> {
  const selections: Record<string, string | number> = {};
  for (const opt of comboProduct.comboVariantOptions) {
    if (opt.combo_product_hidden && opt.combo_product_default_value !== null) {
      selections[opt.name] = opt.combo_product_default_value;
    } else if (!opt.combo_product_hidden) {
      // Use the first visible available value as default
      const firstAvailable = opt.available?.find((a) => !a.disabled) ?? opt.available?.[0];
      if (firstAvailable) {
        selections[opt.name] = firstAvailable.value;
      } else {
        selections[opt.name] = opt.value;
      }
    }
  }
  return selections;
}

// ─── Hook ─────────────────────────────────────────────────────────────────

/**
 * useComboProducts — loads child products for combo bundles, processes their variant
 * options with auto-hide logic, and manages selected combo variants.
 *
 * Ported from Angular widget ProductDetailService.processComboProduct() logic.
 * Supports SPEC-036: PDP Combo Products.
 */
export function useComboProducts(
  parentProduct: ProductDetail | null,
  parentVariant: ProductDetailVariant | null,
  auth: WidgetAuth | null
): UseComboProductsReturn {
  const [comboProducts, setComboProducts] = useState<ProcessedComboProduct[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedComboVariants, setSelectedComboVariants] = useState<Record<number, ProductDetailVariant>>({});
  // Track user option selections per combo product (keyed by combo_product_id)
  const [comboOptionSelections, setComboOptionSelections] = useState<Record<number, Record<string, string | number>>>({});

  const hasComboProducts = !!(
    parentProduct?.combo_products?.ids && parentProduct.combo_products.ids.length > 0
  );

  const comboDiscount = parentProduct?.combo_products?.combo_product_discount ?? '0';

  /**
   * Reset all combo state. Called when parent product changes.
   */
  const reset = useCallback(() => {
    setComboProducts([]);
    setIsLoading(false);
    setError(null);
    setSelectedComboVariants({});
    setComboOptionSelections({});
  }, []);

  /**
   * Load and process combo child products.
   * Called when parent product and parent variant are available and product has combo_products.
   */
  const loadComboProducts = useCallback(
    async (product: ProductDetail, variant: ProductDetailVariant, widgetAuth: WidgetAuth) => {
      const ids = product.combo_products?.ids;
      if (!ids || ids.length === 0) return;

      setIsLoading(true);
      setError(null);
      setComboProducts([]);
      setSelectedComboVariants({});
      setComboOptionSelections({});

      const discount = product.combo_products?.combo_product_discount ?? '0';

      try {
        // Load all child products in parallel
        const results = await Promise.allSettled(
          ids.map((id) => apiClient.getProductDetail(id, widgetAuth))
        );

        const processed: ProcessedComboProduct[] = [];
        let firstError: string | null = null;

        for (const result of results) {
          if (result.status === 'fulfilled') {
            const childProduct = result.value.response;
            const processedCombo = processComboProduct(childProduct, variant, product, discount);
            processed.push(processedCombo);
          } else {
            // AC-06: partial results still usable
            const errMsg =
              result.reason instanceof Error
                ? result.reason.message
                : 'Failed to load combo product';
            if (!firstError) firstError = errMsg;
          }
        }

        setComboProducts(processed);
        if (firstError) setError(firstError);

        // Initialize selectedComboVariants based on default option selections
        const initialVariants: Record<number, ProductDetailVariant> = {};
        const initialSelections: Record<number, Record<string, string | number>> = {};

        for (const combo of processed) {
          const defaultSelections = buildDefaultSelections(combo);
          initialSelections[combo.product.id] = defaultSelections;

          const matchingVariant = findVariantForComboProduct(combo, defaultSelections);
          if (matchingVariant) {
            initialVariants[combo.product.id] = matchingVariant;
          } else if (combo.variants.length > 0) {
            initialVariants[combo.product.id] = combo.variants[0];
          }
        }

        setSelectedComboVariants(initialVariants);
        setComboOptionSelections(initialSelections);
      } catch (err) {
        const errMsg = err instanceof Error ? err.message : 'Failed to load combo products';
        setError(errMsg);
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  // Load combo products when parent product/variant changes
  useEffect(() => {
    if (parentProduct && parentVariant && auth && hasComboProducts) {
      loadComboProducts(parentProduct, parentVariant, auth);
    } else if (!hasComboProducts) {
      // Reset when the product no longer has combo products
      reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parentProduct?.id, parentVariant?.id, auth, hasComboProducts]);

  /**
   * Update the selected variant for a combo product when user changes a dropdown.
   * Finds the combo product, updates its option selections, then finds the matching variant.
   */
  const updateComboVariant = useCallback(
    (comboProductId: number, optionName: string, optionValue: string | number) => {
      // Update option selections
      setComboOptionSelections((prev) => {
        const prevSelections = prev[comboProductId] ?? {};
        const newSelections = { ...prevSelections, [optionName]: optionValue };
        return { ...prev, [comboProductId]: newSelections };
      });

      // Find the combo product and update the selected variant (separate from option selection state update)
      const comboProduct = comboProducts.find((cp) => cp.product.id === comboProductId);
      if (comboProduct) {
        // Build full option set: merge hidden defaults with current user selections + new value
        const fullSelections: Record<string, string | number> = {};
        for (const opt of comboProduct.comboVariantOptions) {
          if (opt.combo_product_hidden && opt.combo_product_default_value !== null) {
            fullSelections[opt.name] = opt.combo_product_default_value;
          } else if (opt.name === optionName) {
            fullSelections[opt.name] = optionValue;
          } else {
            fullSelections[opt.name] = comboOptionSelections[comboProductId]?.[opt.name] ?? opt.value;
          }
        }

        const matchingVariant = findVariantForComboProduct(comboProduct, fullSelections);
        if (matchingVariant) {
          setSelectedComboVariants((prevVariants) => ({
            ...prevVariants,
            [comboProductId]: matchingVariant,
          }));
        }
      }
    },
    [comboProducts, comboOptionSelections]
  );

  return {
    comboProducts,
    isLoading,
    error,
    selectedComboVariants,
    updateComboVariant,
    hasComboProducts,
    comboDiscount,
    reset,
  };
}
